import { useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { useShellRouter } from '@/hooks/useShellRouter';
import { getAuthToken } from '../utils/authToken';
import { useSession } from '../context/SessionContext';
import { mainDomainFor } from '../utils/siyaDomain';
import { useSignInTarget } from '../hooks/useSignInUrl';
import { savePendingDeepLink } from '../utils/pendingRoute';
import StatusCard from './StatusCard';
import { TENANT_NOT_CONFIGURED_STATUS, isTenantNotConfigured } from '../utils/tenantConfig';
import { httpFetch } from '@/lib/httpClient';
import { reportError } from '@/observability/clientLogger';
import { config } from '@/config/runtime';
import AppLoader from '@/components/AppLoader';

interface RouteGuardProps {
  children: ReactNode;
}

interface AppInfo {
  href: string;
  label: string;
  live: boolean;
  updatedAt: string | null;
}

// Routes that don't need permission checks
const PUBLIC_ROUTES = ['/', '/404', '/500', '/_error', '/qhse-initiation-tracker'];

function isPublicPath(pathname: string): boolean {
  if (PUBLIC_ROUTES.includes(pathname) || pathname.startsWith('/api/')) return true;
  // Agent Hub chrome is shell-owned — not in marketplace hrefs.
  if (pathname === '/agent-hub' || pathname.startsWith('/agent-hub/')) return true;
  return false;
}

function formatGoLiveTime(updatedAt: string): string {
  const date = new Date(updatedAt);
  // Add 3 hours
  date.setHours(date.getHours() + 3);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${day}-${month}-${year} ${hours}.${minutes} ${ampm}`;
}

const CACHE_TTL_MS = 10 * 60 * 1000;

// The credential is missing or expired. Distinct from null ("couldn't determine, retry")
// and from an empty Map ("determined: no apps"), because it needs a third response —
// re-authentication — and retrying it is pointless.
const UNAUTHORIZED = 'unauthorized' as const;

// This host has no database configured for the session's tenant. A fourth outcome for the
// same reason there is a third: it is settled (no retry can clear it), it is not a denial
// (the account is fine), and it is not an authentication problem (the session is valid),
// so none of the other three answers is true. Carries the tenant so the card can name it.
const WRONG_ENVIRONMENT = 'wrong_environment' as const;
interface WrongEnvironment {
  kind: typeof WRONG_ENVIRONMENT;
  tenant: string | null;
}

type FetchOutcome = Map<string, AppInfo> | typeof UNAUTHORIZED | WrongEnvironment | null;

const isWrongEnvironment = (outcome: FetchOutcome): outcome is WrongEnvironment =>
  typeof outcome === 'object' && outcome !== null && (outcome as WrongEnvironment).kind === WRONG_ENVIRONMENT;

/**
 * Every state that means "you cannot have this page". They differ in why — see the
 * accessState union — but they share one response: send the user to sign in, then, on a
 * second pass, to the main SIYA app.
 *
 * 'error' is deliberately NOT one of them, though it used to be. It is not a denial: it
 * means the allow-list could not be determined, and the server never said anything about
 * this account. Redirecting on it is actively harmful — an unexplained 500 repeats on the
 * next request, so the sign-in pass returns the identical token and fails identically, and
 * the fallback pass sends the user to the main SIYA host, which for an `onlyMicroapps`
 * account immediately replaces the location back to micro apps (see the SPA's
 * handlePostLoginNavigation). That is a closed circuit between two hosts with a spinner on
 * screen the whole way round and no way for the user to stop it or report it.
 *
 * A server-side fault gets a card that stays put instead. See the 'error' branch below.
 */
const DENIED_STATES = ['denied'] as const;

/**
 * Why 'denied' is the ONLY state that redirects, when all three used to.
 *
 * Redirecting is right for 'denied' and only for it. That user HAS apps and simply cannot
 * have this page, so the second pass lands them on the micro-apps root, which routes them
 * to an app they do have. The journey ends somewhere real.
 *
 * 'no_apps' and 'wrong_environment' end nowhere. Both are settled facts about the account —
 * it has no apps at all, or its session belongs to an environment this host does not serve
 * — so the sign-in pass returns an identical token and an identical answer, and the second
 * pass falls back to the main SIYA host. For the exact users who hit these states that is
 * not an escape, it is a wall: chat-interface/index.html runs
 *
 *     if (!hasVmAccess && onlyMicroapps) window.location.replace(microappsUrl)
 *
 * on EVERY load, not just after a login. So the main host bounces them straight back here,
 * the state resolves the same way, and we send them back again — a closed loop that both
 * sides drive with location.replace, so even the Back button is gone. Verified against the
 * live fleet: for an `onlyMicroapps` account there is no reachable page on the main host.
 *
 * A terminal state gets a card that stays put and says what is true.
 */

const isDenied = (state: string): boolean =>
  (DENIED_STATES as readonly string[]).indexOf(state) !== -1;

/**
 * One automatic sign-in redirect per tab.
 *
 * Without it this is an infinite bounce, and not a hypothetical one: the tenant claim and
 * the role both come from the ACCOUNT, so a user who is denied, sends themselves to login
 * and signs back in as the same person returns with an identical token and is denied
 * again. The browser would shuttle between the two hosts until the tab is closed, with
 * nothing on screen long enough to read.
 *
 * So the second pass goes to the main SIYA app instead. That is a real destination rather
 * than a repeat of what just failed, and it is where someone who cannot use micro apps
 * actually wants to be.
 *
 * Cleared on any successful access check, so a denial later in the same tab — a different
 * app, a session that changed underneath — still gets its own attempt.
 *
 * Deliberately its own key: AuthGuard's marker is cleared the moment a session exists,
 * which is exactly the state every denial here is already in.
 */
const ACCESS_REDIRECT_KEY = 'microappsAccessRedirectTried';

/**
 * How long a denial waits for the login-path probe before giving up and using the main
 * SIYA host instead. Covers useSignInUrl's own request budget plus its retry, so the wait
 * ends only once that hook has genuinely run out of candidates.
 */
const SIGN_IN_WAIT_MS = 15 * 1000;

/**
 * Leave the micro-apps host, breaking out of the chat's Micro Apps panel when we can.
 *
 * Driving the top window is attempted first so sign-in does not load into a small panel
 * and strand the user there. A cross-origin frame cannot do that without a user gesture
 * and throws, which is caught: navigating the frame itself is worse but still reaches the
 * login page, and is the only option left once the card with its target="_top" link is
 * gone.
 */
function leaveTo(url: string): void {
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = url;
      return;
    }
  } catch {
    // Cross-origin parent — fall through and navigate this frame.
  }
  window.location.href = url;
}

// Upper bound on a single permissions fetch. Long enough to survive a cold serverless
// start plus DB connect, short enough that a stuck request doesn't strand the user.
const FETCH_TIMEOUT_MS = 15 * 1000;

// Load cached permissions from sessionStorage to avoid spinner on page reload
const getInitialAppMap = (): { map: Map<string, AppInfo>; hasCached: boolean; isFresh: boolean } => {
  if (typeof window === 'undefined') return { map: new Map(), hasCached: false, isFresh: false };
  try {
    const cached = sessionStorage.getItem('routeGuardApps');
    const cachedAt = Number(sessionStorage.getItem('routeGuardAppsAt') || 0);
    if (cached) {
      const entries = JSON.parse(cached) as [string, AppInfo][];
      // An empty list is never a legitimate allow-list — a signed-in user always has at
      // least one app. Treat a cached [] as a miss so a failed fetch can't lock the user
      // out for the rest of the TTL.
      if (!Array.isArray(entries) || entries.length === 0) {
        return { map: new Map(), hasCached: false, isFresh: false };
      }
      const isFresh = cachedAt > 0 && Date.now() - cachedAt < CACHE_TTL_MS;
      return { map: new Map(entries), hasCached: true, isFresh };
    }
  } catch {}
  return { map: new Map(), hasCached: false, isFresh: false };
};

export default function RouteGuard({ children }: RouteGuardProps) {
  const router = useShellRouter();
  const initialCache = getInitialAppMap();
  // 'reauth' is NOT a denial: the permissions API rejected the credential, so the session
  // is being re-checked and the user is on their way to sign-in. It renders as the spinner
  // so Access Denied never flashes up at someone who is merely signed out.
  // 'no_apps', 'wrong_environment' and 'denied' are still tracked separately even though
  // all three now end in the same redirect. They are different facts — an empty allow-list,
  // a host that cannot serve this tenant, and a route this user is not granted — and the
  // difference decides whether the fetch is retried and whether its result is cached. Only
  // the SCREEN was ever the same.
  const [accessState, setAccessState] = useState<'loading' | 'allowed' | 'denied' | 'no_apps' | 'wrong_environment' | 'under_construction' | 'error' | 'reauth'>(
    initialCache.hasCached ? 'allowed' : 'loading'
  );
  const [appMap, setAppMap] = useState<Map<string, AppInfo>>(initialCache.map);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(initialCache.isFresh);
  const [currentApp, setCurrentApp] = useState<AppInfo | null>(null);

  // Tenant comes from the session (/api/me), not from decoding a token in the browser —
  // in deployed environments the credential is an HttpOnly cookie that JS cannot read.
  const { tenant, status, refresh: refreshSession } = useSession();

  // One re-attempt after a 401. If /api/me still says we are signed in, the rejection was
  // transient (a race with the cookie being refreshed) and refetching resolves it. If the
  // second attempt 401s too, the session really is gone and AuthGuard takes over — but the
  // counter stops the two from ping-ponging.
  const reauthAttempts = useRef(0);

  // x-request-id from the last permissions response, so the error card can name it.
  const requestId = useRef<string | null>(null);

  // Retry is offered, but not endlessly: the two causes seen in production so far — a host
  // not provisioned for the tenant, a claim that will not parse — are identical on every
  // attempt, so a button that keeps promising a different outcome is the same lie the
  // redirect loop told, just slower.
  const [retrying, setRetrying] = useState(false);

  // The tenant a 'wrong_environment' answer named, so the card can say which one.
  const [sessionTenant, setSessionTenant] = useState<string | null>(null);

  // Read once, on mount: has this tab already spent its one automatic sign-in? If so the
  // terminal cards stop offering it, because it has demonstrably not helped.
  const [signInSpent] = useState(() => {
    try {
      return sessionStorage.getItem(ACCESS_REDIRECT_KEY) !== null;
    } catch {
      return true;
    }
  });

  // Returns null when the allow-list could not be retrieved (session not ready, HTTP
  // error, API-level error, network failure). null means "unknown, try again" — it must
  // never be collapsed into an empty Map, which means "user legitimately has no apps".
  // Conflating the two is what produced intermittent Access Denied screens.
  const fetchAppsOnce = useCallback(async (): Promise<FetchOutcome> => {
    if (!tenant) return null;

    // A hung request never rejects, so without this the guard spins forever. Mirrors the
    // timeout safety net the chat interface uses on its embedded iframes.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
      // localhost → Bearer header; deployed → null, and the HttpOnly cookie authenticates.
      const authToken = getAuthToken();
      const nodeEnv = config.nodeEnv;
      const response = await httpFetch(`/api/marketplace/apps?nodeEnv=${nodeEnv}`, {
        headers: {
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          'x-node': tenant,
        },
        signal: controller.signal,
      });

      // Kept for the error card. handleApiError sets this on every 500 and logs the real
      // cause — the env var that was missing, the id that would not parse — under the same
      // id. Putting it on screen is what turns "it says Access Denied" into a support
      // ticket that can be grepped, without exposing what actually failed.
      requestId.current = response.headers.get('x-request-id');

      // 401 means the credential is missing or expired. Reported distinctly so the caller
      // skips its retry — a second request with the same dead credential can only 401
      // again — and re-checks the session instead.
      if (response.status === 401) return UNAUTHORIZED;

      // This host serves a set of tenants and the session's is not among them. Settled
      // and permanent — the pod's configuration will not differ on a retry — so it is
      // reported distinctly rather than falling into the transient bucket below, where it
      // spent its whole life being retried before the user was sent anywhere.
      if (response.status === TENANT_NOT_CONFIGURED_STATUS) {
        const body = await response.json().catch(() => null);
        // Only the code makes it this answer. A 409 from anywhere else is not something to
        // guess about, so it falls through to the transient path exactly as before.
        if (isTenantNotConfigured(body)) {
          return { kind: WRONG_ENVIRONMENT, tenant: body.tenant ?? null };
        }
        return null;
      }

      // 400/403 are the server's considered answer — the user has no role or is not
      // permitted (see /api/marketplace/apps). That is a real denial, so return an empty
      // allow-list. Any other non-OK status (5xx, gateway, timeout) is transient.
      if (!response.ok) {
        return response.status === 400 || response.status === 403 ? new Map() : null;
      }

      const data = await response.json();
      if (data.error) return null;

      const appsList = data.apps || data;
      const map = new Map<string, AppInfo>();
      appsList.forEach((app: any) => {
        map.set(app.href, {
          href: app.href,
          label: app.label,
          live: app.live !== undefined ? app.live : true,
          updatedAt: app.updatedAt || null,
        });
      });

      // Overview is excluded from marketplace but still needs route access
      if (data.hasOverviewAccess && data.overviewMeta) {
        map.set('/overview', {
          href: '/overview',
          label: data.overviewMeta.label || 'SIYA Overview',
          live: data.overviewMeta.live,
          updatedAt: data.overviewMeta.updatedAt,
        });
      }

      return map;
    } catch (error) {
      // AbortError lands here too — a timeout is transient, so null is the right answer.
      // Returning null here means the guard cannot tell "no permission" from
      // "could not ask" — the user is bounced either way, so this is the only
      // record of which one happened.
      reportError(error, { event_detail: 'route_guard_allowed_apps_failed' });
      return null;
    } finally {
      clearTimeout(timeoutId);
    }
    // tenant is required — re-create (and re-fetch) once the session resolves it.
  }, [tenant]);

  // Chat opens micro-apps in a fresh tab, so every one of those loads is a cold
  // sessionStorage cache plus a cold DB connect on the API side. Retry once before
  // giving up, which absorbs the common timeout.
  const fetchApps = useCallback(async (): Promise<FetchOutcome> => {
    const first = await fetchAppsOnce();
    // UNAUTHORIZED is returned to the caller rather than retried — a second request with
    // the same dead credential can only 401 again — and rather than collapsed into null,
    // which would render the Access Denied card at a user who simply needs to sign in.
    if (first !== null) return first;

    await new Promise(resolve => setTimeout(resolve, 600));
    return await fetchAppsOnce();
  }, [fetchAppsOnce]);

  const checkAccess = useCallback(async (pathname: string) => {
    // Skip check for public routes and API routes
    if (isPublicPath(pathname)) {
      setAccessState('allowed');
      return;
    }

    // Use in-memory cache for subsequent navigations (already fetched this session)
    let apps = appMap;

    // Fetch from API on first load, cache in sessionStorage for reload
    if (!hasFetchedOnce) {
      if (apps.size === 0) setAccessState('loading');
      const fetched = await fetchApps();

      // The credential was rejected. Ask /api/me for the verdict: if it agrees, AuthGuard
      // sends the user to sign-in (carrying this URL as returnUrl) and never renders a
      // card at all. Hold the spinner until then — this is an authentication problem, and
      // showing "Access Denied" for it is what made an emailed deep link look like a
      // permissions failure to a user whose session had simply expired.
      if (fetched === UNAUTHORIZED) {
        void refreshSession();
        // Second rejection while /api/me still insists the session is valid. Sign-in
        // cannot be the answer (the user IS signed in), so stop waiting on it and fall
        // back to the retryable card — a spinner that never resolves is worse than a
        // screen with an action on it.
        setAccessState(reauthAttempts.current >= 1 ? 'error' : 'reauth');
        return;
      }

      // Settled: this host cannot serve this tenant at all. Not retried, not cached, and
      // not retried: the pod's configuration cannot differ between two requests a second
      // apart, so the retry only delayed the redirect.
      if (isWrongEnvironment(fetched)) {
        setSessionTenant(fetched.tenant ?? null);
        setAccessState('wrong_environment');
        return;
      }

      // Could not determine the allow-list. Surface a retryable error instead of a false
      // Access Denied, and leave hasFetchedOnce false so the next attempt refetches.
      if (!fetched) {
        setAccessState('error');
        return;
      }

      apps = fetched;
      setAppMap(apps);
      setHasFetchedOnce(true);
      // Cache for page reloads — only a non-empty, known-good list is worth persisting.
      if (apps.size > 0) {
        try {
          sessionStorage.setItem('routeGuardApps', JSON.stringify(Array.from(apps.entries())));
          sessionStorage.setItem('routeGuardAppsAt', String(Date.now()));
        } catch {}
      }
    }

    // A successful fetch that returned nothing means the user genuinely has no apps at all
    // (no role assigned, or no app matches their role and organisation). Real, and final —
    // but a different thing from being refused THIS page, and it gets its own card.
    if (apps.size === 0) {
      setAccessState('no_apps');
      return;
    }

    // Check if current route is in allowed hrefs
    let matchedApp: AppInfo | undefined;
    matchedApp = apps.get(pathname);

    // /survey replaces legacy /class in agent-shell; marketplace may still list /class.
    if (!matchedApp && pathname === '/survey') {
      matchedApp = apps.get('/class') ?? apps.get('/survey');
      if (matchedApp) {
        matchedApp = { ...matchedApp, href: '/survey' };
      }
    }

    // /view/[id] routes — check if /view/{actual-id} is in allowed hrefs.
    // Array.from rather than iterating the Map directly: this tsconfig targets below
    // ES2015 and has no downlevelIteration, so `for..of` over a Map does not compile.
    if (!matchedApp && pathname.startsWith('/view/')) {
      matchedApp = Array.from(apps.values()).find(app => app.href.startsWith('/view/'));
    }

    if (!matchedApp) {
      setAccessState('denied');
      return;
    }

    // Check live
    if (matchedApp.live === false) {
      setCurrentApp(matchedApp);
      setAccessState('under_construction');
      return;
    }

    setAccessState('allowed');
  }, [appMap, fetchApps, hasFetchedOnce, refreshSession]);

  // forceReauth: everyone reaching these cards still holds a working session, so a plain
  // login link is forwarded straight to chat and the form never appears. ?logout clears
  // the session first so credentials can actually be entered.
  //
  // Via the hook, not resolveSignInUrl directly, because ?logout only survives on a path
  // the main host actually routes — on a migrated host the legacy path is swallowed by
  // the SPA's catch-all and the query goes with it, which is the same silent forward this
  // line exists to avoid. The hook also settles WHICH main host, which the shared
  // production host cannot answer from its own name.
  const { url: signInHref, origin: signInOrigin, probeSettled: signInProbeSettled } =
    useSignInTarget(tenant, true);

  // Go Back as a LINK to the main SIYA host, not history.back().
  //
  // history.back() is a dead button in both places these cards actually appear: a tab chat
  // just opened has no previous entry, and neither does a freshly loaded Micro Apps panel.
  // A link always goes somewhere. StatusCard renders links with target="_top", so from
  // inside the panel this returns the whole tab to SIYA rather than loading it into the
  // panel.
  //
  // mainDomainFor strips a `-microapps` label and the shared production host
  // (microapps.siya.com) has none, so it is null on the environment serving the most
  // tenants. Fall back to the host the sign-in probe just proved is real — the same place
  // this was always meant to go — and only then to history.back(), which is left for
  // localhost.
  const mainHref =
    (typeof window !== 'undefined' ? mainDomainFor(window.location.hostname) : null) ?? signInOrigin;

  const handleGoBack = useCallback(() => {
    if (window.history.length > 1) {
      router.back();
      return;
    }
    // Nothing to go back to: the shell root is public and routes by permission.
    void router.replace('/');
  }, [router]);

  const goBackAction = mainHref
    ? { label: 'Go Back', href: mainHref }
    : { label: 'Go Back', onClick: handleGoBack };

  // Check access on mount and route changes
  useEffect(() => {
    checkAccess(router.pathname);
  }, [router.pathname, checkAccess]);

  // The 401 was contradicted by /api/me — the session is live, so the rejection was a
  // blip. Refetch once. If it 401s again the state stays 'reauth' (a spinner) and
  // AuthGuard resolves it, rather than accusing the user of having no permission.
  useEffect(() => {
    if (accessState !== 'reauth' || status !== 'authenticated') return;
    if (reauthAttempts.current >= 1) return;
    reauthAttempts.current += 1;
    void checkAccess(router.pathname);
  }, [accessState, status, router.pathname, checkAccess]);

  // Clear cache when auth token changes (e.g., login/logout)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken') {
        setAppMap(new Map());
        setHasFetchedOnce(false);
        checkAccess(router.pathname);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [router.pathname, checkAccess]);

  // The user asked for a page they could not get. Remember it: if they sign in as an
  // account that does have access, the forced ?logout=true hop loses returnUrl, and this
  // is what puts them back on the page they wanted (see utils/pendingRoute).
  useEffect(() => {
    if (isDenied(accessState) || accessState === 'error') savePendingDeepLink();
  }, [accessState]);

  // A successful check re-arms the one-shot redirect for this tab.
  useEffect(() => {
    if (accessState !== 'allowed') return;
    try {
      sessionStorage.removeItem(ACCESS_REDIRECT_KEY);
    } catch {
      // No sessionStorage: nothing was stored, so nothing to clear.
    }
  }, [accessState]);

  /**
   * Denial sends the user to sign in rather than rendering a card.
   *
   * signInHref carries ?logout=true (useSignInTarget is called with forceReauth). That is
   * load-bearing: both login pages forward a visitor who still has the isLoggedIn flag
   * set straight back without ever showing the form, and every user reaching this effect
   * has it set — they are signed in, they simply cannot have this page. A plain login link
   * here is an instant bounce back to the denial that sent them.
   *
   * Waits for signInHref, which is null until the login-path probe answers. Redirecting to
   * a guessed path is what utils/siyaDomain exists to prevent, so a null href falls
   * through to the main SIYA host and, failing that, leaves the spinner up.
   */
  useEffect(() => {
    if (!isDenied(accessState)) return;

    let tried = false;
    try {
      tried = sessionStorage.getItem(ACCESS_REDIRECT_KEY) !== null;
    } catch {
      // No sessionStorage means no loop guard, so do not start a loop we cannot stop.
      tried = true;
    }

    if (tried) {
      if (mainHref) leaveTo(mainHref);
      return;
    }

    if (signInHref) {
      try {
        sessionStorage.setItem(ACCESS_REDIRECT_KEY, '1');
      } catch {
        return;
      }
      leaveTo(signInHref);
      return;
    }

    // Probe still running — wait for signInHref. The timer is a backstop for a probe that
    // never settles; once probeSettled is true with no href, fall back immediately.
    if (!signInProbeSettled) {
      const timer = setTimeout(() => {
        if (mainHref) leaveTo(mainHref);
      }, SIGN_IN_WAIT_MS);

      return () => clearTimeout(timer);
    }

    if (mainHref) leaveTo(mainHref);
  }, [accessState, signInHref, mainHref, signInProbeSettled]);

  // Settled facts about the account. Neither redirects — see DENIED_STATES for the loop
  // that caused — and both offer sign-in at most once, because a second attempt returns the
  // same account and therefore the same answer.
  if (accessState === 'no_apps' || accessState === 'wrong_environment') {
    const wrongEnvironment = accessState === 'wrong_environment';
    const offerSignIn = !signInSpent && !!signInHref;

    return (
      <StatusCard
        title={wrongEnvironment ? 'Signed In Elsewhere' : 'No Apps Assigned'}
        message={
          wrongEnvironment ? (
            <>
              Your session belongs to{' '}
              <strong>{sessionTenant ?? 'another environment'}</strong>,
              which this site doesn&apos;t serve.
              <br />
              Sign in here to continue.
            </>
          ) : (
            <>
              You&apos;re signed in, but no micro apps have been assigned to your account.
              <br />
              Signing in again won&apos;t change this.
            </>
          )
        }
        note={{
          title: 'Need Help?',
          body: wrongEnvironment
            ? 'If signing in here doesn\u2019t help, contact SIYA Support.'
            : 'Contact SIYA Support to have apps assigned to your account.',
        }}
        actions={
          offerSignIn
            ? [
                {
                  label: wrongEnvironment ? 'Login with SIYA' : 'Sign in as a different user',
                  href: signInHref!,
                  onClick: () => {
                    try {
                      sessionStorage.setItem(ACCESS_REDIRECT_KEY, '1');
                    } catch {
                      // The link still navigates; we just cannot remember that it did.
                    }
                  },
                  variant: wrongEnvironment ? ('primary' as const) : ('secondary' as const),
                },
                { ...goBackAction, variant: 'secondary' as const },
              ]
            : [goBackAction]
        }
      />
    );
  }

  // Something failed on our side and we do not know what. NOT a denial — nothing was said
  // about this account — so it does not redirect: it stays put, says so, and carries the
  // reference that ties it to the log line naming the real cause.
  if (accessState === 'error') {
    return (
      <StatusCard
        title="Something Went Wrong"
        message={<>We couldn&apos;t load your apps.<br />This is a problem on our side, not with your account.</>}
        note={{
          title: 'Need Help?',
          body: requestId.current
            ? `Contact SIYA Support and quote reference ${requestId.current}.`
            : 'If this keeps happening, contact SIYA Support.',
        }}
        actions={[
          {
            label: retrying ? 'Retrying\u2026' : 'Try Again',
            onClick: () => {
              if (retrying) return;
              setRetrying(true);
              void checkAccess(router.pathname).finally(() => setRetrying(false));
            },
            disabled: retrying,
          },
          { ...goBackAction, variant: 'secondary' as const },
        ]}
      />
    );
  }

  // A denial shows the spinner, not a card: leaveTo() is already navigating away. The
  // spinner is what the user sees for the moment the browser takes to leave.
  if (isDenied(accessState) || accessState === 'loading' || accessState === 'reauth') {
    return (
      <AppLoader />
    );
  }

  if (accessState === 'under_construction' && currentApp) {
    return (
      <StatusCard
        title="Under Development"
        message={<><strong>{currentApp.label}</strong> is currently being updated.<br />We are working to bring it back shortly.</>}
        note={currentApp.updatedAt
          ? { title: 'Estimated Go Live', body: `Will be live in ${formatGoLiveTime(currentApp.updatedAt)}`, tone: 'amber' as const }
          : undefined}
        actions={[goBackAction]}
      />
    );
  }

  return <>{children}</>;
}
